import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useStore } from "@/lib/useStore";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProfile } from "@/context/ProfileContext";
import ProfileAvatar from "@/components/ProfileAvatar";
import PinModal from "@/components/PinModal";
import { Plus, Loader2, Settings } from "lucide-react";
import { NotFound } from "@/pages/ErrorPages";

/**
 * Phase 9 — "Who's watching?" picker. Subscribers + tenant admins land here
 * after login. Click avatar → if PIN-locked, open PinModal; otherwise activate
 * profile and route to /home.
 */
export default function SelectProfile() {
  const { slug, sp } = useStore();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");
  const { user, status } = useAuth();
  const { profiles, refreshProfiles, switchProfile, loading } = useProfile();
  const [tenant, setTenant] = useState(null);
  const [tenantStatus, setTenantStatus] = useState("loading");
  const [pendingPin, setPendingPin] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/public/tenants/${slug}`)
      .then((r) => alive && (setTenant(r.data), setTenantStatus("ready")))
      .catch(() => alive && setTenantStatus("not_found"));
    return () => { alive = false; };
  }, [slug]);

  useEffect(() => {
    if (status === "authenticated") {
      refreshProfiles();
    }
  }, [status, refreshProfiles]);

  if (status === "loading" || tenantStatus === "loading") {
    return (
      <div className="min-h-screen bg-[#0a0a0a] grid place-items-center text-white/40 text-xs uppercase tracking-widest" data-testid="select-profile-loading">
        Loading…
      </div>
    );
  }
  if (tenantStatus === "not_found" || !tenant) return <NotFound />;
  if (status !== "authenticated" || !user) {
    navigate(sp(`/login?next=${encodeURIComponent(sp(`/select-profile`))}`), { replace: true });
    return null;
  }

  const themeColor = tenant.theme_color || "#7C3AED";

  const onPick = (p) => {
    if (p.has_pin) {
      setPendingPin(p);
      return;
    }
    switchProfile(p);
    navigate(next || sp(`/home`), { replace: true });
  };

  const onPinSuccess = (p) => {
    switchProfile(p);
    setPendingPin(null);
    navigate(next || sp(`/home`), { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white relative overflow-hidden" data-testid="select-profile-page">
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          background: `radial-gradient(circle at 50% 0%, ${themeColor}33 0%, transparent 60%)`,
        }}
      />
      <div className="relative max-w-5xl mx-auto px-6 py-16 md:py-24">
        <div className="text-center mb-10 md:mb-16">
          <p className="text-[11px] uppercase tracking-[0.4em] text-white/45" data-testid="select-profile-tenant">
            {tenant.name}
          </p>
          <h1 className="mt-3 font-display font-black text-4xl md:text-6xl tracking-tighter">
            Who's watching?
          </h1>
          <p className="mt-3 text-white/55 text-sm md:text-base font-light">
            Pick a profile to continue. Each profile keeps its own watchlist and history.
          </p>
        </div>

        {loading && profiles.length === 0 ? (
          <div className="flex justify-center py-16" data-testid="select-profile-spinner">
            <Loader2 className="w-6 h-6 animate-spin text-white/50" />
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 md:gap-8 justify-items-center">
            {profiles.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => onPick(p)}
                className="group flex flex-col items-center gap-3 transition-transform hover:-translate-y-1"
                data-testid={`select-profile-card-${p.id}`}
                data-profile-name={p.name}
                data-profile-kid={p.is_kid ? "1" : "0"}
              >
                <ProfileAvatar profile={p} size="xl" className="group-hover:ring-2 group-hover:ring-white" />
                <div className="text-center">
                  <div className="text-sm font-medium tracking-tight" data-testid={`select-profile-name-${p.id}`}>
                    {p.name}
                  </div>
                  <div className="mt-0.5 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/40">
                    {p.has_pin && <span data-testid={`select-profile-locked-${p.id}`}>Locked</span>}
                    {p.is_kid && <span data-testid={`select-profile-kid-${p.id}`}>Kid</span>}
                  </div>
                </div>
              </button>
            ))}

            {profiles.length < 5 && (
              <button
                type="button"
                onClick={() => navigate(sp(`/profiles`))}
                className="flex flex-col items-center gap-3 transition-transform hover:-translate-y-1"
                data-testid="select-profile-add"
              >
                <div className="h-28 w-28 grid place-items-center rounded-md border border-dashed border-white/30 hover:border-white text-white/40 hover:text-white transition-colors">
                  <Plus className="w-10 h-10" strokeWidth={1.5} />
                </div>
                <div className="text-sm font-medium text-white/60">Add Profile</div>
              </button>
            )}
          </div>
        )}

        <div className="mt-12 md:mt-16 flex justify-center">
          <button
            type="button"
            onClick={() => navigate(sp(`/profiles`))}
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] border border-white/15 hover:border-white px-5 h-11 text-white/65 hover:text-white"
            data-testid="select-profile-manage"
          >
            <Settings className="w-3.5 h-3.5" /> Manage Profiles
          </button>
        </div>
      </div>

      {pendingPin && (
        <PinModal
          profile={pendingPin}
          onSuccess={onPinSuccess}
          onCancel={() => setPendingPin(null)}
        />
      )}
    </div>
  );
}
